import { ONE_DAY_MS } from '@eflight/shared';

import { aircraftMaintenanceLimitExtensionFromDb } from '../../Admin/AircraftMaintenanceLimitExtensions/AircraftMaintenanceLimitExtension';
import { aircraftMaintenanceLimitFromDb } from '../../Admin/AircraftMaintenanceLimits/AircraftMaintenanceLimit';
import { aircraftMaintenanceLimitExtensionItemFields } from '../../Admin/Datasources/AircraftMaintenanceLimitExtensions';
import { type CreateAircraftMaintenanceLimitExtensionInput, type CreateAircraftMaintenanceLimitInput } from '../../API';
import { type ContactInput } from '../../APIExtensions';
import { getDb } from './LocalDB';
import { getStandardItemHandler } from './standardItemHandler';
import {
    type DbSyncerUpDefinition,
    type DbSyncSubscription,
    type DbSyncSubscriptionAdditionalData,
    type SyncableTable,
} from './syncHelpers';
import { wrapHandlerWithAccumulator } from './syncHelpers';
import { aircraftMaintenanceLimitExtensionSyncerDefinition } from './Transformers/aircraftMaintenanceLimitExtensions';
import { aircraftFromDb, aircraftItemFields } from './Transformers/aircrafts';
import { contactTlbItemFields } from './Transformers/contacts';
import {
    countableChangeFromDb,
    countableChangeItemFields,
    countableChangeSyncerDefinition,
} from './Transformers/countableChanges';
import { dutyItemItemFields, dutyItemToDb, dutyItemTransform } from './Transformers/dutyItems';
import {
    flightDocumentItemFields,
    flightDocumentItemHandler,
    flightDocumentToDb,
} from './Transformers/flightDocuments';
import { flightExpenseItemFields, flightExpenseItemHandler, flightExpenseToDb } from './Transformers/flightExpenses';
import { flightLegFromDb, flightLegItemFields, flightLegSyncerDefinition } from './Transformers/flightLegs';
import { flightItemFields, flightToDb, flightTransform } from './Transformers/flights';
import { internalContactFromDb, internalContactSyncItemFields } from './Transformers/internalContacts';
import { logbookRecordItemFields, logbookRecordToDb } from './Transformers/logbookRecords';
import {
    releaseToServiceFromDb,
    releaseToServiceItemFields,
    releaseToServiceSyncerDefinition,
} from './Transformers/releaseToServices';
import { signedDataToDb } from './Transformers/signedDatas';
import { signingKeyChangeToDb } from './Transformers/signingKeyChanges';
import { squawkDeferralFromDb, squawkDeferralItemFields, squawkDeferralToDb } from './Transformers/squawkDeferrals';
import { squawkFromDb, squawkItemFields, squawkSyncerDefinition } from './Transformers/squawks';
import { type DbCreated, type DbItems } from './types';

export const aircraftsResyncSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncAircraft(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString}) {
              items {
                ${aircraftItemFields}
                flights(limit:10, sortDirection: DESC) {
                  items {
                    ${flightItemFields}
                    legs(limit: 99) {
                      items {
                        ${flightLegItemFields}
                      }
                    }
                    flightDocuments(limit:10, sortDirection: DESC) {
                      items {
                        ${flightDocumentItemFields}
                      }
                    }
                    flightExpenses(limit: 30, sortDirection: DESC) {
                      items {
                        ${flightExpenseItemFields}
                      }
                    }
                  }
                }
                squawks(limit:20, sortDirection: DESC) {
                  items {
                    ${squawkItemFields}
                    squawkDeferrals(limit: 100) {
                        items {
                            ${squawkDeferralItemFields}
                        }
                    }
                    flightDocuments(limit:100, sortDirection: DESC) {
                      items {
                        ${flightDocumentItemFields}
                      }
                    }
                    releaseToServices(limit: 3, sortDirection: DESC) {
                      items {
                        ${releaseToServiceItemFields}
                        flightDocuments(limit:100, sortDirection: DESC) {
                          items {
                            ${flightDocumentItemFields}
                          }
                        }
                      }
                    }
                  }
                }
                countableChanges(limit:20, sortDirection: DESC) {
                  items {
                    ${countableChangeItemFields}
                  }
                }
                releaseToServices(limit: 5, sortDirection: DESC) {
                  items {
                    ${releaseToServiceItemFields}
                    flightDocuments(limit:100, sortDirection: DESC) {
                      items {
                        ${flightDocumentItemFields}
                      }
                    }
                  }
                }
              }
            }`;
    },
    key: 'syncAircraft',
    onNewItems: async (items) => {
        const aircrafts = items.map(aircraftFromDb);
        const dbHandlers = getDbHandlers();
        const {
            flightHandler,
            legHandler,
            aircraftHandler,
            squawkHandler,
            squawkDeferralHandler,
            releaseToServiceHandler,
            countableChangeChandler,
            flightDocumentHandler,
            flightExpenseHandler,
        } = dbHandlers;
        const promises: Array<Promise<void>> = [];
        promises.push(aircraftHandler(aircrafts));
        items.forEach((aircraft) => {
            if (aircraft.flights.items?.length) {
                const flights = aircraft.flights.items.map(flightTransform);
                aircraft.flights.items.forEach((flight: any) => {
                    if (flight.legs?.items?.length) {
                        const legs = flight.legs.items.map(flightLegFromDb);
                        promises.push(legHandler(legs));
                    }
                    if (flight.flightDocuments?.items?.length) {
                        promises.push(flightDocumentHandler(flight.flightDocuments.items));
                    }
                    if (flight.flightExpenses?.items?.length) {
                        promises.push(flightExpenseHandler(flight.flightExpenses.items));
                    }
                });
                promises.push(flightHandler(flights));
            }
            if (aircraft.squawks.items?.length) {
                const squawks = aircraft.squawks.items.map(squawkFromDb);
                promises.push(squawkHandler(squawks));
                aircraft.squawks.items.forEach((squawk: any) => {
                    if (squawk.flightDocuments.items?.length) {
                        promises.push(flightDocumentHandler(squawk.flightDocuments.items));
                    }
                    if (squawk.squawkDeferrals.items?.length) {
                        const squawkDeferrals = squawk.squawkDeferrals.items.map(squawkDeferralFromDb);
                        promises.push(squawkDeferralHandler(squawkDeferrals));
                    }
                    if (squawk.releaseToServices.items?.length) {
                        const releaseToServices = squawk.releaseToServices.items.map(releaseToServiceFromDb);
                        promises.push(releaseToServiceHandler(releaseToServices));
                        for (const releaseToService of squawk.releaseToServices.items) {
                            if (releaseToService.flightDocuments.items?.length) {
                                promises.push(flightDocumentHandler(releaseToService.flightDocuments.items));
                            }
                        }
                    }
                });
            }
            if (aircraft.countableChanges.items?.length) {
                const countableChanges = aircraft.countableChanges.items.map(countableChangeFromDb);
                promises.push(countableChangeChandler(countableChanges));
            }
            if (aircraft.releaseToServices.items?.length) {
                const releaseToServices = aircraft.releaseToServices.items.map(releaseToServiceFromDb);
                promises.push(releaseToServiceHandler(releaseToServices));
                for (const releaseToService of aircraft.releaseToServices.items) {
                    if (releaseToService.flightDocuments.items?.length) {
                        promises.push(flightDocumentHandler(releaseToService.flightDocuments.items));
                    }
                }
            }
        });
        Object.values(dbHandlers).forEach(async (handlerWrapper) => handlerWrapper.start());
        await Promise.all(promises);
    },
};

const getDbHandlers = function () {
    const db = getDb();
    const flightHandler = wrapHandlerWithAccumulator(getStandardItemHandler(db.flights));
    const legHandler = wrapHandlerWithAccumulator(getStandardItemHandler(db.legs));
    const aircraftHandler = wrapHandlerWithAccumulator(getStandardItemHandler(db.aircrafts));
    const squawkHandler = wrapHandlerWithAccumulator(getStandardItemHandler(db.squawks));
    const countableChangeChandler = wrapHandlerWithAccumulator(getStandardItemHandler(db.countableChanges));
    const squawkDeferralHandler = wrapHandlerWithAccumulator(getStandardItemHandler(db.squawkDeferrals));
    const releaseToServiceHandler = wrapHandlerWithAccumulator(getStandardItemHandler(db.releaseToServices));
    const flightDocumentHandler = wrapHandlerWithAccumulator(flightDocumentItemHandler);
    const flightExpenseHandler = wrapHandlerWithAccumulator(flightExpenseItemHandler);

    return {
        flightHandler,
        legHandler,
        aircraftHandler,
        squawkHandler,
        squawkDeferralHandler,
        releaseToServiceHandler,
        countableChangeChandler,
        flightDocumentHandler,
        flightExpenseHandler,
    };
};

export const aircraftsSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncAircraft(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString}) {
              items {
                ${aircraftItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncAircraft',
    onNewItems: getStandardItemHandler(getDb().aircrafts, aircraftFromDb),
};

export const flightsSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncFlights(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString}) {
              items {
                ${flightItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncFlights',
    onNewItems: getStandardItemHandler(getDb().flights, flightTransform),
};

export const flightDocumentsSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncFlightDocuments(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString}) {
              items {
                ${flightDocumentItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncFlightDocuments',
    onNewItems: flightDocumentItemHandler,
};

export const flightExpensesSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncFlightExpenses(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString}) {
              items {
                ${flightExpenseItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncFlightExpenses',
    onNewItems: flightExpenseItemHandler,
};

export const flightLegsSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncLegs(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString}) {
              items {
                ${flightLegItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncLegs',
    onNewItems: getStandardItemHandler(getDb().legs, flightLegFromDb),
};

export const squawksSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncSquawks(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString}) {
              items {
                ${squawkItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncSquawks',
    onNewItems: getStandardItemHandler(getDb().squawks, squawkFromDb),
};

export const squawkDeferralsSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncSquawkDeferrals(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString}) {
              items {
                ${squawkDeferralItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncSquawkDeferrals',
    onNewItems: getStandardItemHandler(getDb().squawkDeferrals, squawkDeferralFromDb),
};

export const releaseToServicesSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncReleaseToServices(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString}) {
              items {
                ${releaseToServiceItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncReleaseToServices',
    onNewItems: getStandardItemHandler(getDb().releaseToServices, releaseToServiceFromDb),
};

export const countableChangesSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncCountableChanges(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString}) {
              items {
                ${countableChangeItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncCountableChanges',
    onNewItems: getStandardItemHandler(getDb().countableChanges, countableChangeFromDb),
};

const aircraftDocumentItemFields = `id
    deleted
    createdAt
    aircraftId
    contentKey
    name
    documentNumber
    issueDate
    validUntilDate
    validUntilDateToleranceSeconds
    validUntilCountableTypes
    validUntilCountableValues
    validUntilCountableValuesTolerance
    _version`;

export const aircraftDocumentsSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncAircraftDocuments(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString},filter:{deleted: {eq: false}}) {
              items {
                ${aircraftDocumentItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncAircraftDocuments',
    onNewItems: getStandardItemHandler(getDb().aircraftDocuments),
};
const aircraftMaintenanceLimitItemFields = `id
    deleted
    createdAt
    aircraftId
    name
    validUntilDate
    validUntilDateToleranceSeconds
    validUntilCountableTypes
    validUntilCountableValues
    validUntilCountableValuesTolerance
    customWarningRules
    limitExtensionRules
    _version`;
export const aircraftMaintenanceLimitsSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncAircraftMaintenanceLimits(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString},filter:{deleted: {eq: false}}) {
              items {
                ${aircraftMaintenanceLimitItemFields}
                extensions(sortDirection: DESC) {
                    items {
                        ${aircraftMaintenanceLimitExtensionItemFields}
                    }
                }
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncAircraftMaintenanceLimits',
    onNewItems: async (
        items: Array<
            CreateAircraftMaintenanceLimitInput &
                DbCreated & { extensions: DbItems<CreateAircraftMaintenanceLimitExtensionInput> }
        >
    ) => {
        const itemsBare: Array<CreateAircraftMaintenanceLimitInput & DbCreated> = [];
        const extensionsBare: Array<CreateAircraftMaintenanceLimitExtensionInput & DbCreated> = [];
        for (const item of items) {
            const { extensions, ...bare } = item;
            itemsBare.push(bare);
            extensionsBare.push(...extensions.items);
        }
        await Promise.all([
            getStandardItemHandler(
                getDb().aircraftMaintenanceLimitExtensions,
                aircraftMaintenanceLimitExtensionFromDb
            )(extensionsBare),
            getStandardItemHandler(getDb().aircraftMaintenanceLimits, aircraftMaintenanceLimitFromDb)(itemsBare),
        ]);
    },
};

export const aircraftMaintenanceLimitExtensionsSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncAircraftMaintenanceLimitExtensions(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString},filter:{deleted: {eq: false}}) {
              items {
                ${aircraftMaintenanceLimitExtensionItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncAircraftMaintenanceLimitExtensions',
    onNewItems: getStandardItemHandler(
        getDb().aircraftMaintenanceLimitExtensions,
        aircraftMaintenanceLimitExtensionFromDb
    ),
};

export const dutyItemsSubscription: DbSyncSubscription = {
    getQuery: (nextToken, { currentUserId, isInitialSync }) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;
        const userIdString = currentUserId ? `"${currentUserId}"` : 'null';
        const limit = isInitialSync ? 300 : 10;

        return `
            syncDutyItems: dutyItemByUserId(userId:${userIdString},sortDirection: DESC,limit:${limit},nextToken:${nextTokenString},filter:{deleted: {eq: false}}) {
              items {
                ${dutyItemItemFields}
                reports {
                    items {
                        id
                        status
                    }
                }
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncDutyItems',
    nextTokenGetter: (data: any, meta: DbSyncSubscriptionAdditionalData) => {
        if (!meta.isInitialSync) {
            return undefined;
        }

        const { nextToken, items } = data;
        if (!nextToken) {
            return undefined;
        }
        if (items.length) {
            const lastItem = items.at(-1);
            const oldestRequired = new Date(Date.now() - 366 * ONE_DAY_MS);
            if (lastItem.startDate > oldestRequired.toISOString()) {
                return nextToken;
            }
        }

        return undefined;
    },
    onNewItems: getStandardItemHandler(getDb().dutyItems, dutyItemTransform),
};

export const contactsResyncSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            listContactsByIsCrew(isActiveCrew: "x",limit:9999,nextToken:${nextTokenString}) {
              items {
                ${contactTlbItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'listContactsByIsCrew',
    onNewItems: async (items) => getStandardItemHandler(getDb().contacts)(items),
};

export const contactsSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncContacts(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString}) {
              items {
                ${contactTlbItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncContacts',
    onNewItems: async (items: ContactInput[]) => {
        const crewables = items.filter((i) => i.isActiveCrew);

        return getStandardItemHandler(getDb().contacts)(crewables);
    },
};

export const logbookRecordsSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncLogbookRecords(lastSync:$lastSync,limit:20,nextToken:${nextTokenString}) {
              items {
                ${logbookRecordItemFields}
              }
              startedAt
            }`;
    },
    key: 'syncLogbookRecords',
    onNewItems: getStandardItemHandler(getDb().logbookRecords),
};

export const internalContactsResyncSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            listInternalContacts(limit:9999,nextToken:${nextTokenString},filter:{deleted:{eq:false}}) {
              items {
                ${internalContactSyncItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'listInternalContacts',
    onNewItems: getStandardItemHandler(getDb().internalContacts, internalContactFromDb),
};

export const internalContactsSubscription: DbSyncSubscription = {
    getQuery: (nextToken) => {
        const nextTokenString = nextToken ? `"${nextToken}"` : `null`;

        return `
            syncInternalContacts(lastSync:$lastSync,limit:9999,nextToken:${nextTokenString}) {
              items {
                ${internalContactSyncItemFields}
              }
              nextToken
              startedAt
            }`;
    },
    key: 'syncInternalContacts',
    onNewItems: getStandardItemHandler(getDb().internalContacts, internalContactFromDb),
};

export const dbSyncerUpDefintions: Map<SyncableTable, DbSyncerUpDefinition> = new Map<
    SyncableTable,
    DbSyncerUpDefinition
>();

dbSyncerUpDefintions.set('flights', flightToDb);
dbSyncerUpDefintions.set('legs', flightLegSyncerDefinition);
dbSyncerUpDefintions.set('countableChanges', countableChangeSyncerDefinition);
dbSyncerUpDefintions.set('squawks', squawkSyncerDefinition);
dbSyncerUpDefintions.set('squawkDeferrals', squawkDeferralToDb);
dbSyncerUpDefintions.set('releaseToServices', releaseToServiceSyncerDefinition);
dbSyncerUpDefintions.set('documents', flightDocumentToDb);
dbSyncerUpDefintions.set('flightExpenses', flightExpenseToDb);
dbSyncerUpDefintions.set('dutyItems', dutyItemToDb);
dbSyncerUpDefintions.set('signingKeyChanges', signingKeyChangeToDb);
dbSyncerUpDefintions.set('signedData', signedDataToDb);
dbSyncerUpDefintions.set('logbookRecords', logbookRecordToDb);
dbSyncerUpDefintions.set('aircraftMaintenanceLimitExtensions', aircraftMaintenanceLimitExtensionSyncerDefinition);

export const dbSyncerResyncSubscriptions: DbSyncSubscription[] = [
    contactsResyncSubscription,
    internalContactsResyncSubscription,
    aircraftsResyncSubscription,
    aircraftDocumentsSubscription,
    aircraftMaintenanceLimitsSubscription,
    dutyItemsSubscription,
    logbookRecordsSubscription,
];

export const dbSyncerUpdateSubscriptions: DbSyncSubscription[] = [
    contactsSubscription,
    internalContactsSubscription,
    aircraftsSubscription,
    aircraftDocumentsSubscription,
    aircraftMaintenanceLimitsSubscription,
    aircraftMaintenanceLimitExtensionsSubscription,
    countableChangesSubscription,
    flightLegsSubscription,
    flightDocumentsSubscription,
    flightExpensesSubscription,
    flightsSubscription,
    squawksSubscription,
    releaseToServicesSubscription,
    squawkDeferralsSubscription,
    dutyItemsSubscription,
    logbookRecordsSubscription,
];
